/**
 * Users Query Hooks
 * React Query hooks for user data fetching and mutations
 * Used by the Users management tab (src/Pages/Management)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usersService } from 'src/api/services';
import { useToast } from 'src/Contexts/ToastContext';
import { queryKeys } from './queryKeys';
import type { User } from 'src/types';

/**
 * Hook to fetch all users
 *
 * @returns React Query result with users list
 */
export function useUsersQuery() {
  return useQuery({
    queryKey: queryKeys.users.lists(),
    queryFn: () => usersService.getAll(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch a single user by ID
 *
 * @param id - User ID
 * @returns React Query result with user data
 */
export function useUserQuery(id: string) {
  return useQuery({
    queryKey: queryKeys.users.detail(id),
    queryFn: () => usersService.getById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch users by project
 *
 * @param projectId - Project ID
 * @returns React Query result with project users
 */
export function useUsersByProjectQuery(projectId: string) {
  return useQuery({
    queryKey: queryKeys.users.byProject(projectId),
    queryFn: () => usersService.getByProject(projectId),
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Mutation hook to create a new user
 *
 * @returns React Query mutation result
 */
export function useCreateUserMutation() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (data: Partial<User>) => usersService.create(data),

    // Optimistic update
    onMutate: async (newUser) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.users.all });

      const previousUsers = queryClient.getQueryData(queryKeys.users.lists());

      queryClient.setQueryData<User[]>(queryKeys.users.lists(), (old = []) => [
        ...old,
        { ...newUser, id: `temp-${Date.now()}` } as User,
      ]);

      return { previousUsers };
    },

    onError: (err: any, _newUser, context) => {
      if (context?.previousUsers) {
        queryClient.setQueryData(queryKeys.users.lists(), context.previousUsers);
      }
      const message = err?.apiError?.message || 'Failed to create user';
      toast.error(message);
      console.error('Create user error:', err);
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      toast.success('User created successfully');
    },
  });
}

/**
 * Mutation hook to update an existing user
 *
 * @returns React Query mutation result
 */
export function useUpdateUserMutation() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<User> }) =>
      usersService.update(id, data),

    // Optimistic update
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.users.all });

      const previousUsers = queryClient.getQueryData(queryKeys.users.lists());

      queryClient.setQueryData<User[]>(queryKeys.users.lists(), (old = []) =>
        old.map((user) => (user.id === id ? { ...user, ...data } : user))
      );

      queryClient.setQueryData<User>(queryKeys.users.detail(id), (old) =>
        old ? { ...old, ...data } : old
      );

      return { previousUsers };
    },

    onError: (err: any, { id: _id }, context) => {
      if (context?.previousUsers) {
        queryClient.setQueryData(queryKeys.users.lists(), context.previousUsers);
      }
      const message = err?.apiError?.message || 'Failed to update user';
      toast.error(message);
      console.error('Update user error:', err);
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      toast.success('User updated successfully');
    },
  });
}

/**
 * Mutation hook to delete a user
 *
 * @returns React Query mutation result
 */
export function useDeleteUserMutation() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (id: string) => usersService.delete(id),

    // Optimistic update
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.users.all });

      const previousUsers = queryClient.getQueryData(queryKeys.users.lists());

      queryClient.setQueryData<User[]>(queryKeys.users.lists(), (old = []) =>
        old.filter((user) => user.id !== id)
      );

      return { previousUsers };
    },

    onError: (err: any, _id, context) => {
      if (context?.previousUsers) {
        queryClient.setQueryData(queryKeys.users.lists(), context.previousUsers);
      }
      const message = err?.apiError?.message || 'Failed to delete user';
      toast.error(message);
      console.error('Delete user error:', err);
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      toast.success('User deleted successfully');
    },
  });
}
